import type { ExtraKind, ScriptMark } from "../api";

/**
 * The performance script's marks on the client (ADR-0157): a paragraph is its
 * source with marks inserted at byte offsets, and this is the client's copy of
 * narration/script's Apply, so an edit to one mark can be sent back as the
 * paragraph's script. The server checks it strictly; nothing here is trusted.
 */

const encoder = new TextEncoder();

/** stringIndex maps a byte offset in source (UTF-8, the server's unit) to a string index. */
export function stringIndex(source: string, byte: number): number {
  let bytes = 0;
  let index = 0;
  for (const ch of source) {
    if (bytes >= byte) break;
    bytes += encoder.encode(ch).length;
    index += ch.length;
  }
  return index;
}

/** A pause, then a speaker, then a cue at one offset: `said. [pause] {older man: x} [cue] "Hi"`. */
function order(kind: ScriptMark["kind"]): number {
  switch (kind) {
    case "pause":
    case "long_pause":
      return 0;
    case "speaker":
      return 1;
    case "cue":
      return 2;
    default:
      return 3;
  }
}

export function sortMarks(marks: ScriptMark[]): ScriptMark[] {
  return [...marks].sort((a, b) => a.at - b.at || order(a.kind) - order(b.kind));
}

/** markText is how a mark is written into the script. */
export function markText(mark: ScriptMark): string {
  switch (mark.kind) {
    case "pause":
      return " [pause]";
    case "long_pause":
      return " [long pause]";
    case "cue":
      return `[${mark.text ?? ""}] `;
    case "speaker":
      return `{${(mark.extra ?? "").replace(/_/g, " ")}: ${mark.text ?? ""}} `;
    case "stop":
      return ".";
  }
}

/** Segment is a run of the author's text, or one mark. */
export type Segment = { kind: "text"; text: string } | { kind: "mark"; mark: ScriptMark };

/** segments cuts a paragraph into its text and its marks, in reading order. */
export function segments(source: string, marks: ScriptMark[]): Segment[] {
  const out: Segment[] = [];
  let at = 0;
  for (const mark of sortMarks(marks)) {
    const i = stringIndex(source, mark.at);
    if (i < at) continue;
    if (i > at) out.push({ kind: "text", text: source.slice(at, i) });
    out.push({ kind: "mark", mark });
    at = i;
    // A stop replaces the comma it sits on.
    if (mark.kind === "stop" && source[at] === ",") at++;
  }
  if (at < source.length) out.push({ kind: "text", text: source.slice(at) });
  return out;
}

/** applyMarks is the paragraph's script: narration/script.Apply. */
export function applyMarks(source: string, marks: ScriptMark[]): string {
  return segments(source, marks)
    .map((seg) => (seg.kind === "text" ? seg.text : markText(seg.mark)))
    .join("");
}

/** withCue sets the cue at a quote's start, or removes it when cue is blank. */
export function withCue(marks: ScriptMark[], at: number, cue: string): ScriptMark[] {
  const rest = marks.filter((m) => !(m.kind === "cue" && m.at === at));
  const words = cue.replace(/[[\]]/g, "").trim().replace(/\s+/g, " ");
  return words ? [...rest, { at, kind: "cue", text: words }] : rest;
}

/**
 * withSpeaker sets a line's speaker mark — who says it and the extra voice
 * that reads it — or removes it when name is blank.
 */
export function withSpeaker(
  marks: ScriptMark[],
  at: number,
  name: string,
  extra: ExtraKind,
): ScriptMark[] {
  const rest = marks.filter((m) => !(m.kind === "speaker" && m.at === at));
  const words = name
    .replace(/[{}[\]:"]/g, "")
    .trim()
    .replace(/\s+/g, " ");
  return words ? [...rest, { at, kind: "speaker", text: words, extra }] : rest;
}

/**
 * withSpeakerKind gives every speaker mark named `name` (ignoring case) the
 * extra voice `extra`: the studio keeps one voice per speaker in a chapter by
 * rewriting every line of the name (ADR-0157 §9). It returns the marks and
 * whether any changed.
 */
export function withSpeakerKind(
  marks: ScriptMark[],
  name: string,
  extra: ExtraKind,
): [ScriptMark[], boolean] {
  let changed = false;
  const key = name.toLowerCase();
  const out = marks.map((m) => {
    if (m.kind !== "speaker" || (m.text ?? "").toLowerCase() !== key || m.extra === extra) return m;
    changed = true;
    return { ...m, extra };
  });
  return [out, changed];
}

/** nextPause cycles a beat's pause: none, pause, long pause, none. */
export function nextPause(marks: ScriptMark[], at: number): ScriptMark[] {
  const here = marks.find((m) => m.at === at && (m.kind === "pause" || m.kind === "long_pause"));
  const rest = marks.filter((m) => m !== here);
  if (!here) return [...rest, { at, kind: "pause" }];
  if (here.kind === "pause") return [...rest, { at, kind: "long_pause" }];
  return rest;
}

/** byteLength is a paragraph's length in the server's unit. */
export function byteLength(source: string): number {
  return encoder.encode(source).length;
}
